import React, { useState } from "react";
import { SOLCELLEVASK_INFO } from "../data/siteData";

export default function SolcellevaskCalculator({ onSelectPlan }) {
  const [panelCount, setPanelCount] = useState(20);
  const [selectedPlanId, setSelectedPlanId] = useState("halvaar");

  const pricePerPanel = SOLCELLEVASK_INFO.pricePerPanel; // 22 kr.
  const pricePerWash = panelCount * pricePerPanel;

  const presets = [
    { label: "12 paneler", count: 12 },
    { label: "18 paneler", count: 18 },
    { label: "24 paneler", count: 24 },
    { label: "36 paneler", count: 36 }
  ];

  const handleApplyToForm = (plan) => {
    if (onSelectPlan) {
      onSelectPlan({
        panelCount,
        planName: plan?.name || "Halvårsabonnement",
        pricePerWash
      });
    }
    const formEl = document.getElementById("tilbud") || document.getElementById("kontakt");
    if (formEl) {
      formEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      id="beregner"
      className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-8 lg:p-10 scroll-mt-28 sm:scroll-mt-32"
    >
      <div className="max-w-3xl mx-auto text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-bold uppercase tracking-wider mb-3">
          <i className="fa-solid fa-calculator text-cyan-600" aria-hidden="true" />
          <span>Prisberegner</span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          Beregn prisen på din solcellevask
        </h3>
        <p className="mt-2 text-slate-600 text-sm sm:text-base">
          Vælg dit antal solcellepaneler og se din faste pris med det samme. Kun 22 kr. pr. panel.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Side: Summary & Total Card */}
        <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 rounded-none p-6 sm:p-7 text-white shadow-xl flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center justify-between border-b border-slate-700/80 pb-4 mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                <i className="fa-solid fa-solar-panel" aria-hidden="true" />
                Overslag &amp; Sammenfatning
              </span>
              <span className="text-xs text-slate-300 font-medium bg-slate-800/80 px-2 py-0.5 rounded">
                Inkl. moms
              </span>
            </div>

            <div className="space-y-3.5 text-sm text-slate-300">
              <div className="flex justify-between items-center">
                <span>Antal solcellepaneler:</span>
                <span className="font-bold text-white text-base">{panelCount} stk.</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Pris pr. panel:</span>
                <span className="font-bold text-cyan-400">{pricePerPanel} kr.</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Valgt abonnement:</span>
                <span className="font-bold text-white">
                  {selectedPlanId === "halvaar"
                    ? "Halvårsabonnement (2x/år)"
                    : selectedPlanId === "aar"
                    ? "Årsabonnement (1x/år)"
                    : "Enkeltstående vask"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Vaskemetode:</span>
                <span className="text-xs text-slate-200 text-right">
                  Ultra-rent osmosevand (uden kemi)
                </span>
              </div>
            </div>

            <div className="my-6 pt-5 border-t border-slate-700">
              <div className="text-xs text-cyan-300 uppercase tracking-wider font-semibold mb-1">
                Pris pr. vask
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                  {pricePerWash.toLocaleString("da-DK")}
                </span>
                <span className="text-lg font-medium text-slate-300">kr. inkl. moms</span>
              </div>

              {selectedPlanId === "halvaar" && (
                <p className="mt-2 text-xs text-slate-300 flex items-center gap-1.5">
                  <i className="fa-solid fa-calendar-check text-cyan-400" aria-hidden="true" />
                  <span>Vaskes forår & efterår – svarende til {(pricePerWash * 2).toLocaleString("da-DK")} kr./år</span>
                </p>
              )}

              {selectedPlanId === "aar" && (
                <p className="mt-2 text-xs text-slate-300 flex items-center gap-1.5">
                  <i className="fa-solid fa-calendar-check text-cyan-400" aria-hidden="true" />
                  <span>Vaskes 1 gang årligt forud for sommersæsonen</span>
                </p>
              )}

              <div className="mt-4 p-3 bg-cyan-950/60 border border-cyan-500/30 rounded-lg text-xs text-cyan-100 flex items-start gap-2.5">
                <i className="fa-solid fa-circle-check text-cyan-400 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  <strong>Maksimal virkningsgrad:</strong> Rene paneler kan forbedre din el-produktion med op til 10–25% afhængig af tilsmudsning.
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleApplyToForm(SOLCELLEVASK_INFO.subscriptions.find((s) => s.id === selectedPlanId))}
            className="w-full mt-4 py-3.5 px-6 rounded-full bg-cyan-500 hover:bg-cyan-400 active:bg-cyan-600 text-slate-950 font-bold text-sm sm:text-base transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-0.5 transform active:scale-95 shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Bestil tilbud på {panelCount} paneler</span>
            <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
          </button>
        </div>

        {/* Right Side: Interactive Controls */}
        <div className="lg:col-span-7 space-y-6">
          {/* Panel Count Slider & Input */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 sm:p-6">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="panel-count-range" className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <i className="fa-solid fa-solar-panel text-cyan-600" aria-hidden="true" />
                Hvor mange solcellepaneler har du?
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id="panel-count-input"
                  min="4"
                  max="120"
                  value={panelCount}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      setPanelCount(Math.min(120, Math.max(1, val)));
                    }
                  }}
                  className="w-20 px-2.5 py-1 text-right font-bold text-slate-900 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base"
                />
                <span className="text-xs font-semibold text-slate-600">stk.</span>
              </div>
            </div>

            <input
              type="range"
              id="panel-count-range"
              min="4"
              max="60"
              step="1"
              value={panelCount}
              onChange={(e) => setPanelCount(parseInt(e.target.value, 10))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-600 focus:outline-none"
            />
            <div className="flex justify-between text-xs text-slate-600 mt-1.5 font-medium">
              <span>4 paneler</span>
              <span>20 paneler (typisk villa)</span>
              <span>60+ paneler</span>
            </div>

            {/* Quick Presets */}
            <div className="mt-4 pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-600 mb-2 font-medium">Hurtigvælg standardstørrelse:</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {presets.map((p) => (
                  <button
                    key={p.count}
                    type="button"
                    onClick={() => setPanelCount(p.count)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                      panelCount === p.count
                        ? "bg-cyan-600 text-white border-cyan-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Subscription Model Choice */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-3">
              Vælg aftaletype:
            </label>
            <div className="space-y-3">
              {SOLCELLEVASK_INFO.subscriptions.map((sub) => {
                const isSelected = selectedPlanId === sub.id;
                return (
                  <div
                    key={sub.id}
                    onClick={() => setSelectedPlanId(sub.id)}
                    className={`cursor-pointer rounded-xl border p-4 transition-all duration-200 ${
                      isSelected
                        ? "bg-cyan-50/50 border-cyan-500 shadow-sm ring-1 ring-cyan-500"
                        : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/70"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border mt-0.5 flex items-center justify-center shrink-0 ${
                            isSelected
                              ? "border-cyan-600 bg-cyan-600 text-white"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {isSelected && <i className="fa-solid fa-check text-[10px]" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900 text-sm sm:text-base">
                              {sub.name}
                            </span>
                            {sub.badge && (
                              <span
                                className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                  sub.recommended
                                    ? "bg-cyan-500 text-white"
                                    : "bg-slate-100 text-slate-700 border border-slate-200"
                                }`}
                              >
                                {sub.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                            {sub.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-xs text-slate-600 block">Frekvens</span>
                        <span className="text-xs font-bold text-slate-900">{sub.frequency}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <i className="fa-solid fa-shield-check text-cyan-600 text-base shrink-0" aria-hidden="true" />
            <span>
              <strong>Ingen binding:</strong> Alle vores abonnementer er uden bindingsperiode. Du kan altid opsige, ændre eller sætte på pause.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
